import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  startAfter,
  Timestamp,
  GeoPoint,
  getCountFromServer
} from 'firebase/firestore';
import { db } from './firebase';

// Types
export interface BusinessListing {
  id?: string;
  basic: {
    title: string;
    description: string;
    shortDescription: string;
    category: string;
    subcategory: string;
    tags: string[];
    highlights?: string[];
    seoTitle?: string;
    seoUrl?: string;
    buyerTypes?: string[];
    aiMetadata?: {
      titlesProcessed?: boolean;
      categoriesProcessed?: boolean;
      highlightsProcessed?: boolean;
      seoProcessed?: boolean;
      buyerTypesProcessed?: boolean;
      lastProcessed?: Timestamp;
    };
    status: 'active' | 'pending' | 'sold' | 'expired' | 'draft' | 'pending_payment';
    featured: boolean;
    verified: boolean;
    createdAt: Timestamp;
    updatedAt: Timestamp;
    expiresAt: Timestamp;
    publishedAt?: Timestamp;
  };
  location: {
    address: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
    coordinates: GeoPoint;
    region: string;
    timezone: string;
  };
  financial: {
    askingPrice: number;
    currency: string;
    annualRevenue: number;
    annualCashFlow: number;
    annualExpenses: number;
    profitMargin: number;
    growthRate: number;
    pricePerRevenue: number;
    pricePerCashFlow: number;
    financingAvailable: boolean;
  };
  business: {
    established: number;
    employees: {
      min: number;
      max: number;
    };
    squareFootage: number;
    leaseTerms: string;
    reasonForSale: string;
    trainingIncluded: boolean;
    relocatable: boolean;
    workFromHome: boolean;
    absenteeOwnership: boolean;
    franchise: boolean;
  };
  media: {
    images: string[];
    videos: string[];
    documents: string[];
    featuredImage: string;
  };
  owner: {
    userId: string;
    brokerId?: string;
    contactInfo: {
      name: string;
      email: string;
      phone: string;
      preferredContact: 'email' | 'phone';
    };
    responseTime: number;
  };
  search: {
    searchableText: string;
    priceRange: 'under_100k' | '100k_500k' | '500k_1m' | '1m_5m' | 'over_5m';
    revenueRange: 'under_100k' | '100k_500k' | '500k_1m' | '1m_5m' | 'over_5m';
    locationKeywords: string[];
    categoryKeywords: string[];
  };
  package?: {
    id: string;
    name: string;
    price: number;
    currency: string;
    purchasedAt: Timestamp;
    expiresAt: Timestamp;
    stripeSessionId?: string;
    stripePaymentIntentId?: string;
    stripeCustomerId?: string;
  };
  payment?: {
    completed: boolean;
    completedAt: Timestamp;
    amount: number;
    currency: string;
  };
}

export interface BusinessFilters {
  category?: string;
  subcategory?: string;
  province?: string;
  city?: string;
  priceMin?: number;
  priceMax?: number;
  revenueMin?: number;
  revenueMax?: number;
  status?: 'active' | 'pending' | 'sold' | 'expired' | 'draft' | 'pending_payment';
  featured?: boolean;
  verified?: boolean;
  workFromHome?: boolean;
  relocatable?: boolean;
  franchise?: boolean;
}

export interface BusinessSort {
  field: 'basic.createdAt' | 'basic.updatedAt' | 'financial.askingPrice' | 'financial.annualRevenue' | 'analytics.views';
  direction: 'asc' | 'desc';
}

// Business Service Class
class BusinessService {
  private collection = 'businesses';

  // Create a new business listing
  async createBusiness(businessData: Omit<BusinessListing, 'id' | 'basic.createdAt' | 'basic.updatedAt'>): Promise<string> {
    try {
      const now = Timestamp.now();
      const businessWithDefaults: Omit<BusinessListing, 'id'> = {
        ...businessData,
        basic: {
          ...businessData.basic,
          createdAt: now,
          updatedAt: now,
          status: businessData.basic.status || 'pending',
          featured: businessData.basic.featured || false,
          verified: businessData.basic.verified || false,
        }
      };

      const docRef = await addDoc(collection(db, this.collection), businessWithDefaults);
      return docRef.id;
    } catch (error) {
      console.error('Error creating business:', error);
      throw error;
    }
  }

  // Get a single business by ID or SEO URL
  async getBusiness(idOrSeoUrl: string): Promise<BusinessListing | null> {
    try {
      // First, try to find by SEO URL in businesses collection
      const businessesQuery = query(
        collection(db, this.collection),
        where('basic.seoUrl', '==', idOrSeoUrl),
        limit(1)
      );
      const businessesSnapshot = await getDocs(businessesQuery);
      
      if (!businessesSnapshot.empty) {
        const doc = businessesSnapshot.docs[0];
        return { id: doc.id, ...doc.data() } as BusinessListing;
      }
      
      // Try to find by SEO URL in ddfListings collection
      const ddfQuery = query(
        collection(db, 'ddfListings'),
        where('basic.seoUrl', '==', idOrSeoUrl),
        limit(1)
      );
      const ddfSnapshot = await getDocs(ddfQuery);
      
      if (!ddfSnapshot.empty) {
        const doc = ddfSnapshot.docs[0];
        return { id: `ddf-${doc.id}`, ...doc.data() } as BusinessListing;
      }
      
      // Fall back to ID lookup (for backward compatibility)
      // Check if it's a DDF listing (prefixed with 'ddf-')
      if (idOrSeoUrl.startsWith('ddf-')) {
        const listingKey = idOrSeoUrl.replace('ddf-', '');
        const docRef = doc(db, 'ddfListings', listingKey);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          return { id: idOrSeoUrl, ...docSnap.data() } as BusinessListing;
        }
        return null;
      }

      // Regular user-submitted listing by ID
      const docRef = doc(db, this.collection, idOrSeoUrl);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() } as BusinessListing;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error getting business:', error);
      throw error;
    }
  }

  // Get businesses with filtering and pagination (optimized for performance)
  // Uses parallel queries and proper server-side pagination for fast loading
  async getBusinesses(
    filters: BusinessFilters = {},
    sort: BusinessSort = { field: 'basic.createdAt', direction: 'desc' },
    pageSize: number = 20,
    lastDoc?: any,
    options?: { 
      includeDDF?: boolean;
      fetchMoreForClientFiltering?: boolean; // Fetch more when client-side filtering is needed (search, buyer types, age)
    }
  ): Promise<{ businesses: BusinessListing[]; lastDoc: any; total: number; hasMore: boolean }> {
    try {
      // When category/province filters are applied, fetch enough so frontend sees same counts as admin
      const hasCategoryOrLocationFilter = !!(filters.category || filters.subcategory || filters.province);
      const effectivePageSize = hasCategoryOrLocationFilter
        ? Math.max(pageSize, 3500) // Fetch full matching set when filtering by category/province
        : options?.fetchMoreForClientFiltering
          ? Math.min(Math.max(pageSize * 2, 200), 1000) // Fetch 2x pageSize, but cap at 1000
          : pageSize;

      // Build query constraints for user-submitted businesses
      let q = collection(db, this.collection);
      const constraints: any[] = [];

      // Apply filters
      // Default to excluding draft listings unless explicitly requested
      if (filters.status) {
        constraints.push(where('basic.status', '==', filters.status));
      } else {
        // Exclude drafts by default - only show active, pending, sold, or expired
        // Use 'in' operator to include multiple statuses
        constraints.push(where('basic.status', 'in', ['active', 'pending', 'sold', 'expired']));
      }
      if (filters.category) {
        constraints.push(where('basic.category', '==', filters.category));
      }
      if (filters.subcategory) {
        constraints.push(where('basic.subcategory', '==', filters.subcategory));
      }
      if (filters.province) {
        constraints.push(where('location.province', '==', filters.province));
      }
      if (filters.city) {
        constraints.push(where('location.city', '==', filters.city));
      }
      if (filters.priceMin !== undefined) {
        constraints.push(where('financial.askingPrice', '>=', filters.priceMin));
      }
      if (filters.priceMax !== undefined) {
        constraints.push(where('financial.askingPrice', '<=', filters.priceMax));
      }
      if (filters.featured !== undefined) {
        constraints.push(where('basic.featured', '==', filters.featured));
      }
      if (filters.verified !== undefined) {
        constraints.push(where('basic.verified', '==', filters.verified));
      }
      if (filters.workFromHome !== undefined) {
        constraints.push(where('business.workFromHome', '==', filters.workFromHome));
      }
      if (filters.relocatable !== undefined) {
        constraints.push(where('business.relocatable', '==', filters.relocatable));
      }
      if (filters.franchise !== undefined) {
        constraints.push(where('business.franchise', '==', filters.franchise));
      }

      // Apply sorting
      constraints.push(orderBy(sort.field, sort.direction));

      // Apply pagination
      constraints.push(limit(effectivePageSize));
      if (lastDoc) {
        constraints.push(startAfter(lastDoc));
      }

      // Execute queries in parallel for better performance
      const includeDDF = options?.includeDDF !== false; // Default to true for backward compatibility
      
      // Build user businesses query
      const userQuery = getDocs(query(q, ...constraints));
      
      // Build DDF query with same category/province filters so frontend sees same counts as admin
      const ddfLimit = hasCategoryOrLocationFilter
        ? Math.max(effectivePageSize, 3000) // Fetch enough DDF when filtering so counts match admin
        : effectivePageSize;
      const ddfConstraints: any[] = [
        where('basic.status', '==', 'active'),
      ];
      if (filters.category) {
        ddfConstraints.push(where('basic.category', '==', filters.category));
      }
      if (filters.subcategory) {
        ddfConstraints.push(where('basic.subcategory', '==', filters.subcategory));
      }
      if (filters.province) {
        ddfConstraints.push(where('location.province', '==', filters.province));
      }
      if (filters.priceMin !== undefined && filters.priceMin > 0) {
        ddfConstraints.push(where('financial.askingPrice', '>=', filters.priceMin));
      }
      if (filters.priceMax !== undefined && filters.priceMax > 0) {
        ddfConstraints.push(where('financial.askingPrice', '<=', filters.priceMax));
      }
      ddfConstraints.push(orderBy(sort.field, sort.direction));
      ddfConstraints.push(limit(ddfLimit));
      // Note: we do not use lastDoc for DDF (it's from user snapshot); DDF is merged in memory

      const ddfQueryPromise = includeDDF 
        ? getDocs(query(collection(db, 'ddfListings'), ...ddfConstraints)).catch((ddfError) => {
            console.warn('Error loading DDF listings (continuing with user-submitted businesses only):', ddfError);
            return { forEach: () => {}, docs: [] }; // Return empty snapshot-like object
          })
        : Promise.resolve({ forEach: () => {}, docs: [] });

      // Execute all queries in parallel
      const [userSnapshot, ddfSnapshot] = await Promise.all([userQuery, ddfQueryPromise]);
      
      const businesses: BusinessListing[] = [];
      
      // Process user-submitted businesses
      userSnapshot.forEach((doc) => {
        businesses.push({ id: doc.id, ...doc.data() } as BusinessListing);
      });

      // Process DDF listings (if available)
      if (ddfSnapshot && ddfSnapshot.forEach) {
        ddfSnapshot.forEach((doc: any) => {
          businesses.push({ id: `ddf-${doc.id}`, ...doc.data() } as BusinessListing);
        });
      }

      // Sort combined results (since we can't sort across collections)
      businesses.sort((a, b) => {
        if (sort.field === 'financial.askingPrice') {
          return sort.direction === 'desc' 
            ? b.financial.askingPrice - a.financial.askingPrice
            : a.financial.askingPrice - b.financial.askingPrice;
        }
        const aTime = a.basic.createdAt?.toMillis?.() || 0;
        const bTime = b.basic.createdAt?.toMillis?.() || 0;
        return sort.direction === 'desc' ? bTime - aTime : aTime - bTime;
      });

      // When category/province filter is applied, return full set so frontend count matches admin
      // Otherwise for server-side pagination return only the requested pageSize
      const paginatedBusinesses = hasCategoryOrLocationFilter || options?.fetchMoreForClientFiltering
        ? businesses
        : businesses.slice(0, pageSize);
      
      const lastVisible = userSnapshot.docs[userSnapshot.docs.length - 1];
      const hasMore = userSnapshot.docs.length === effectivePageSize || 
                      (includeDDF && ddfSnapshot && ddfSnapshot.docs && ddfSnapshot.docs.length === effectivePageSize) ||
                      businesses.length > pageSize;
      
      return {
        businesses: paginatedBusinesses,
        lastDoc: lastVisible,
        total: businesses.length, // This is approximate when paginating
        hasMore
      };
    } catch (error) {
      console.error('Error getting businesses:', error);
      throw error;
    }
  }

  // Update a business listing
  async updateBusiness(id: string, updates: Partial<BusinessListing>): Promise<void> {
    try {
      const docRef = doc(db, this.collection, id);
      const updateData = {
        ...updates,
        'basic.updatedAt': Timestamp.now()
      };
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error updating business:', error);
      throw error;
    }
  }

  // Activate business listing after payment
  async activateBusinessListing(id: string, packageId: string): Promise<void> {
    try {
      const docRef = doc(db, this.collection, id);
      const now = Timestamp.now();
      
      // Calculate expiration date based on package
      let expirationDate: Timestamp;
      switch (packageId) {
        case '3months':
          expirationDate = new Timestamp(now.seconds + (90 * 24 * 60 * 60), 0); // 90 days
          break;
        case '6months':
          expirationDate = new Timestamp(now.seconds + (180 * 24 * 60 * 60), 0); // 180 days
          break;
        case '12months':
          expirationDate = new Timestamp(now.seconds + (365 * 24 * 60 * 60), 0); // 1 year (until sold)
          break;
        case 'full':
          expirationDate = new Timestamp(now.seconds + (365 * 24 * 60 * 60), 0); // 1 year
          break;
        default:
          expirationDate = new Timestamp(now.seconds + (90 * 24 * 60 * 60), 0); // Default 90 days
      }

      const updateData = {
        'basic.status': 'active',
        'basic.publishedAt': now,
        'basic.expiresAt': expirationDate,
        'basic.updatedAt': now,
        'package': {
          id: packageId,
          purchasedAt: now,
          expiresAt: expirationDate
        }
      };
      
      await updateDoc(docRef, updateData);
    } catch (error) {
      console.error('Error activating business listing:', error);
      throw error;
    }
  }

  // Delete a business listing
  async deleteBusiness(id: string): Promise<void> {
    try {
      // Check if it's a DDF listing (prefixed with 'ddf-')
      if (id.startsWith('ddf-')) {
        const listingKey = id.replace('ddf-', '');
        const docRef = doc(db, 'ddfListings', listingKey);
        await deleteDoc(docRef);
      } else {
        // Regular user-submitted listing
        const docRef = doc(db, this.collection, id);
        await deleteDoc(docRef);
      }
    } catch (error) {
      console.error('Error deleting business:', error);
      throw error;
    }
  }

  // Get all businesses (for admin)
  async getAllBusinesses(): Promise<BusinessListing[]> {
    try {
      // Get user-submitted businesses
      const q = query(
        collection(db, this.collection),
        orderBy('basic.createdAt', 'desc')
      );

      const querySnapshot = await getDocs(q);
      
      const businesses: BusinessListing[] = [];
      querySnapshot.forEach((doc) => {
        businesses.push({ id: doc.id, ...doc.data() } as BusinessListing);
      });

      // Also get DDF listings
      try {
        const ddfQuery = query(
          collection(db, 'ddfListings'),
          where('basic.status', '==', 'active')
        );

        const ddfSnapshot = await getDocs(ddfQuery);
        ddfSnapshot.forEach((doc) => {
          businesses.push({ id: `ddf-${doc.id}`, ...doc.data() } as BusinessListing);
        });
      } catch (ddfError) {
        console.warn('Error loading DDF listings for admin (continuing with user-submitted businesses only):', ddfError);
        // Continue without DDF listings rather than failing completely
      }

      // Sort combined results
      businesses.sort((a, b) => {
        const aTime = a.basic.createdAt?.toMillis?.() || 0;
        const bTime = b.basic.createdAt?.toMillis?.() || 0;
        return bTime - aTime; // Descending order
      });

      return businesses;
    } catch (error) {
      console.error('Error getting all businesses:', error);
      throw error;
    }
  }

  // Get featured businesses
  async getFeaturedBusinesses(limitCount: number = 10): Promise<BusinessListing[]> {
    try {
      // Get featured user-submitted businesses
      const q = query(
        collection(db, this.collection),
        where('basic.featured', '==', true),
        where('basic.status', '==', 'active'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const businesses: BusinessListing[] = [];
      
      querySnapshot.forEach((doc) => {
        businesses.push({ id: doc.id, ...doc.data() } as BusinessListing);
      });

      // Also get featured DDF listings (we can mark some as featured later)
      // For now, just get some active DDF listings
      try {
        const ddfQuery = query(
          collection(db, 'ddfListings'),
          where('basic.status', '==', 'active'),
          limit(Math.max(0, limitCount - businesses.length))
        );

        const ddfSnapshot = await getDocs(ddfQuery);
        ddfSnapshot.forEach((doc) => {
          businesses.push({ id: `ddf-${doc.id}`, ...doc.data() } as BusinessListing);
        });
      } catch (ddfError) {
        console.warn('Error loading DDF listings for featured businesses (continuing with user-submitted businesses only):', ddfError);
        // Continue without DDF listings rather than failing completely
      }

      // Sort in memory
      businesses.sort((a, b) => {
        const aTime = a.basic.createdAt?.toMillis?.() || 0;
        const bTime = b.basic.createdAt?.toMillis?.() || 0;
        return bTime - aTime; // Descending order
      });

      return businesses.slice(0, limitCount);
    } catch (error) {
      console.error('Error getting featured businesses:', error);
      throw error;
    }
  }

  // Search businesses by text
  async searchBusinesses(searchTerm: string, limitCount: number = 20): Promise<BusinessListing[]> {
    try {
      // Note: Firestore doesn't support full-text search natively
      // This is a basic implementation. For production, consider using Algolia or similar
      // Temporary workaround: Remove ordering until index is built
      const q = query(
        collection(db, this.collection),
        where('basic.status', '==', 'active'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(q);
      const businesses: BusinessListing[] = [];
      
      querySnapshot.forEach((doc) => {
        const business = { id: doc.id, ...doc.data() } as BusinessListing;
        // Basic client-side filtering
        if (business.search.searchableText.toLowerCase().includes(searchTerm.toLowerCase())) {
          businesses.push(business);
        }
      });

      // Sort in memory instead
      businesses.sort((a, b) => {
        const aTime = a.basic.createdAt?.toMillis?.() || 0;
        const bTime = b.basic.createdAt?.toMillis?.() || 0;
        return bTime - aTime; // Descending order
      });

      return businesses;
    } catch (error) {
      console.error('Error searching businesses:', error);
      throw error;
    }
  }

  // Get all active businesses for listings page (no pagination limit, excludes drafts)
  async getAllActiveBusinesses(): Promise<BusinessListing[]> {
    try {
      const businesses: BusinessListing[] = [];
      
      // Fetch all user-submitted businesses (excluding drafts)
      const userBusinessesQuery = query(
        collection(db, this.collection),
        where('basic.status', 'in', ['active', 'pending', 'sold', 'expired']),
        orderBy('basic.createdAt', 'desc')
      );
      
      const userSnapshot = await getDocs(userBusinessesQuery);
      userSnapshot.forEach((doc) => {
        businesses.push({ id: doc.id, ...doc.data() } as BusinessListing);
      });

      // Fetch all DDF listings (only active ones)
      try {
        const ddfQuery = query(
          collection(db, 'ddfListings'),
          where('basic.status', '==', 'active')
        );
        
        const ddfSnapshot = await getDocs(ddfQuery);
        ddfSnapshot.forEach((doc) => {
          businesses.push({ id: `ddf-${doc.id}`, ...doc.data() } as BusinessListing);
        });
      } catch (ddfError) {
        console.warn('Error loading DDF listings (continuing with user-submitted businesses only):', ddfError);
      }

      // Sort combined results by creation date (newest first)
      businesses.sort((a, b) => {
        const aTime = a.basic.createdAt?.toMillis?.() || 0;
        const bTime = b.basic.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });

      return businesses;
    } catch (error) {
      console.error('Error getting all active businesses:', error);
      throw error;
    }
  }

  // Get total count of active businesses (for homepage stats)
  // Uses getCountFromServer for fast count queries without fetching documents
  // Optimized with parallel queries for better performance
  async getTotalActiveBusinessCount(): Promise<number> {
    try {
      // Build both count queries
      const userBusinessesQuery = query(
        collection(db, this.collection),
        where('basic.status', 'in', ['active', 'pending', 'sold', 'expired'])
      );
      
      const ddfQuery = query(
        collection(db, 'ddfListings'),
        where('basic.status', '==', 'active')
      );
      
      // Execute count queries in parallel for better performance
      const [userBusinessesCountSnapshot, ddfCountSnapshot] = await Promise.all([
        getCountFromServer(userBusinessesQuery),
        getCountFromServer(ddfQuery).catch((ddfError) => {
          console.warn('Error loading DDF count (continuing with user businesses only):', ddfError);
          return { data: () => ({ count: 0 }) }; // Return zero count on error
        })
      ]);
      
      const userBusinessesCount = userBusinessesCountSnapshot.data().count;
      const ddfCount = ddfCountSnapshot.data().count;
      
      return userBusinessesCount + ddfCount;
    } catch (error) {
      console.error('Error getting total business count:', error);
      // Return a default value if there's an error
      return 0;
    }
  }

  // Get businesses by owner
  async getBusinessesByOwner(userId: string): Promise<BusinessListing[]> {
    try {
      // Temporary workaround: Remove ordering until index is built
      const q = query(
        collection(db, this.collection),
        where('owner.userId', '==', userId)
      );

      const querySnapshot = await getDocs(q);
      const businesses: BusinessListing[] = [];
      
      querySnapshot.forEach((doc) => {
        businesses.push({ id: doc.id, ...doc.data() } as BusinessListing);
      });

      // Sort in memory instead
      businesses.sort((a, b) => {
        const aTime = a.basic.createdAt?.toMillis?.() || 0;
        const bTime = b.basic.createdAt?.toMillis?.() || 0;
        return bTime - aTime; // Descending order
      });

      return businesses;
    } catch (error) {
      console.error('Error getting businesses by owner:', error);
      throw error;
    }
  }
}

export const businessService = new BusinessService();
export default businessService; 